/**
 * Weekly workload, on the portal home — Jay 2026-09-12, placed here by Max the same day
 * ("tôi nghĩ anh ấy muốn xây ở chỗ này á. Bạn có thể làm cho admin ko, những người khác thì sẽ ko có").
 *
 * Jay's question was whether the trackers keep a status history. Project Tracker does; Sample Tracker
 * does not, because a round's status is DERIVED from its dates and is stored nowhere — so "how many
 * did Jenny have in production three weeks ago" cannot be asked of today's data. The Monday job writes
 * the answer down instead, and this is the block that reads it back.
 *
 * It renders numbers that were settled on the day they describe. Nothing here recomputes anything: a
 * week that was measured stays as it was measured, and a week that was RECONSTRUCTED afterwards says
 * so on the column, because the two are not the same evidence.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, Info } from 'lucide-react';
import './HomeWorkload.css';

const shortWeek = (iso) => (iso || '').slice(5).replace('-', '/');   // 2026-09-07 -> 09/07

const HomeWorkload = ({ data }) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState('rounds');
  const weeks = data?.weeks || [];
  const valueOf = (cell) => {
    if (!cell) return null;
    return metric === 'rounds' ? cell.roundsActive : cell.projects;
  };
  /* A PIC who carried nothing of THIS metric in any week on screen is not a row — three lines of
     zeros under the people doing the work say nothing, and the other toggle brings them back. */
  const pics = (data?.pics || []).filter((p) => weeks.some((w) => valueOf(p.byWeek[w.weekOf])));
  if (!weeks.length || !pics.length) return null;
  const lastWeek = weeks[weeks.length - 1]?.weekOf;
  const prevWeek = weeks[weeks.length - 2]?.weekOf;
  const columnTotal = (weekOf) => pics.reduce((a, p) => a + (valueOf(p.byWeek[weekOf]) || 0), 0);

  /* null = there is nothing to compare against; 0 = compared and unchanged. Rendering both as blank
     made "steady at 1" look like missing data. */
  const delta = (p) => {
    const now = valueOf(p.byWeek[lastWeek]);
    const before = valueOf(p.byWeek[prevWeek]);
    if (now === null || before === null) return null;
    return now - before;
  };

  return (
    <section className="hw-wrap">
      <header className="hw-head">
        <TrendingUp size={15} />
        <h2>{t('home.workload.title')}</h2>
        <span className="hw-sub">{t('home.workload.sub')}</span>
        <div className="hw-toggle">
          <button
            type="button"
            className={metric === 'rounds' ? 'on' : ''}
            onClick={() => setMetric('rounds')}
          >
            {t('home.workload.rounds')}
          </button>
          <button
            type="button"
            className={metric === 'projects' ? 'on' : ''}
            onClick={() => setMetric('projects')}
          >
            {t('home.workload.projects')}
          </button>
        </div>
      </header>

      <div className="hw-scroll">
        <table className="hw-table">
          <thead>
            <tr>
              <th className="hw-pic">{t('home.workload.pic')}</th>
              {weeks.map((w) => (
                <th key={w.weekOf} className={`hw-num ${w.weekOf === lastWeek ? 'hw-now' : ''}`}>
                  {shortWeek(w.weekOf)}
                  {/* A week worked out after the fact is marked, not hidden. */}
                  {w.reconstructed && <em title={t('home.workload.reconstructed')}>*</em>}
                </th>
              ))}
              <th className="hw-num hw-delta-h">{t('home.workload.change')}</th>
            </tr>
          </thead>
          <tbody>
            {pics.map((p) => {
              const d = delta(p);
              return (
                <tr key={p.pic}>
                  <td className="hw-pic" title={p.pic}>{p.pic}</td>
                  {weeks.map((w) => {
                    const v = valueOf(p.byWeek[w.weekOf]);
                    const cell = p.byWeek[w.weekOf];
                    const title = cell && metric === 'rounds'
                      ? t('home.workload.cellRounds', {
                        prod: cell.inProduction, pending: cell.pendingOutcome, wait: cell.rmWaiting,
                      })
                      : undefined;
                    return (
                      <td
                        key={w.weekOf}
                        className={`hw-num ${w.weekOf === lastWeek ? 'hw-now' : ''}`}
                        title={title}
                      >
                        {v === null ? <span className="hw-none">·</span> : v}
                      </td>
                    );
                  })}
                  <td className={`hw-num hw-delta ${d > 0 ? 'up' : ''} ${d < 0 ? 'down' : ''}`}>
                    {d === null ? '' : (d === 0 ? '—' : `${d > 0 ? '+' : ''}${d}`)}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="hw-pic">{t('home.workload.total')}</td>
              {weeks.map((w) => (
                <td key={w.weekOf} className={`hw-num ${w.weekOf === lastWeek ? 'hw-now' : ''}`}>
                  {columnTotal(w.weekOf)}
                </td>
              ))}
              <td className="hw-num" />
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="hw-foot">
        <Info size={12} />
        {t('home.workload.note')}
      </p>
    </section>
  );
};

export default HomeWorkload;
